import { z } from "zod";
import { Id, IsoDate } from "./common.ts";
import { Job, MediaAsset, Post } from "./domain.ts";
import type { RouteDef } from "./routes.ts";

/**
 * Channel media in OUR storage (ALP-77 media fix) + reload from Instagram with post version history.
 *
 * Instagram CDN URLs (`scontent-*.cdninstagram.com/...&oe=<hex unix expiry>`) expire within days: every image Malfak shows
 * for channel content (post media, carousel children, video posters, commenter/contact and account profile pictures, DM
 * attachments) is copied into object storage by the worker (`MirrorMedia`, background, retried, size-capped, rate-limited)
 * and served with signed read URLs. Until a copy exists the API falls back to the channel URL marked `mirrored: false`;
 * the web shows a placeholder when it breaks and asks `refreshPostMedia` (fresh URLs from the Graph API → re-mirror).
 *
 * Post versions: a snapshot (caption + media) whenever the content changes — on Instagram (seen by a reload or the periodic
 * sync: `instagram_edit`), in Malfak (composer caption / media edits: `malfak_edit`) or at import (`import`). Restoring a
 * version is local only: the Instagram Graph API cannot edit a published caption or media (`POST /<IG_MEDIA_ID>` only
 * accepts `comment_enabled`, docs/platforms/instagram/03-ref-ig-media.md "Updating").
 */
export const PostVersionSource = z.enum(["import", "instagram_edit", "malfak_edit"]);
export const PostVersion = z.object({
  id: Id,
  postId: Id,
  version: z.number().int().min(1),
  caption: z.string(),
  /** Media of this version in order (assets that still exist; may be shorter than mediaExternalIds while mirroring). */
  media: z.array(MediaAsset),
  /** Channel ids of the media in order (empty for Malfak-only posts). */
  mediaExternalIds: z.array(z.string()),
  altTexts: z.array(z.string().nullable()).optional(),
  source: PostVersionSource,
  contentHash: z.string(),
  createdBy: Id.nullable(),
  /** When this content was seen (fetch time for channel versions, edit time for Malfak edits). */
  createdAt: IsoDate,
  /** The version the post shows now. */
  current: z.boolean(),
});
export const PostVersions = z.object({ items: z.array(PostVersion) });

export const ReloadPostsRequest = z.object({
  /** Reload these posts (max 200); with neither field every imported post of the org is reloaded. */
  postIds: z.array(Id).max(200).optional(),
  /** Reload every post of this account. */
  accountId: Id.optional(),
  /** Also fetch new comments (existing comments sync / reconcile). Default true. */
  includeComments: z.boolean().default(true),
});

/** Per-org view of what is still not in our storage (admin console + the repair job). */
export const MediaRepairOrg = z.object({
  orgId: Id,
  name: z.string(),
  postsMissing: z.number().int(),
  avatarsMissing: z.number().int(),
  attachmentsMissing: z.number().int(),
  failed: z.number().int(),
  lastJob: Job.nullable(),
});
export const MediaRepairStatus = z.object({
  orgs: z.array(MediaRepairOrg),
  /** Last start of a media repair job anywhere (null = never). */
  lastRunAt: IsoDate.nullable(),
  videosMirrored: z.boolean(),
  maxImageMb: z.number(),
  maxVideoMb: z.number(),
});
export const RunMediaRepairRequest = z.object({ orgId: Id.optional(), refreshAvatars: z.boolean().default(false) });
export const RunMediaRepairResult = z.object({ workflowId: z.string(), started: z.boolean() });

const Org = z.object({ orgId: z.string().uuid() });
const OrgPost = Org.extend({ postId: z.string().uuid() });
const Empty = z.object({});
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;

export const mediaRoutes = {
  refreshPostMedia: route({ method: "POST", path: "/v1/orgs/:orgId/posts/:postId/media/refresh", params: OrgPost, body: Empty, response: Job, status: 202, summary: "Fetch fresh media URLs from the channel and re-copy the post's media into storage (job media_refresh; one in flight per post)" }),
  refreshAccountMedia: route({ method: "POST", path: "/v1/orgs/:orgId/accounts/:accountId/media/refresh", params: Org.extend({ accountId: z.string().uuid() }), body: Empty, response: Job, status: 202, summary: "Re-copy every post's media and the profile pictures of one account (job media_refresh)" }),
  repairOrgMedia: route({ method: "POST", path: "/v1/orgs/:orgId/media/repair", params: Org, body: Empty, response: Job, status: 202, summary: "Copy every channel image of the org that is not in storage yet (posts, avatars, DM attachments): re-runnable job media_repair (admin)" }),
  reloadPost: route({ method: "POST", path: "/v1/orgs/:orgId/posts/:postId/reload", params: OrgPost, body: Empty, response: Job, status: 202, summary: "Reload the post from Instagram: caption, media (mirrored), type/children, permalink, counts, insights, new comments; a change records a post version (job post_reload)" }),
  reloadPosts: route({ method: "POST", path: "/v1/orgs/:orgId/posts/reload", params: Org, body: ReloadPostsRequest, response: Job, status: 202, summary: "Reload many posts (selection, an account, or every imported post) from Instagram (job post_reload)" }),
  postVersions: route({ method: "GET", path: "/v1/orgs/:orgId/posts/:postId/versions", params: OrgPost, response: PostVersions, summary: "Version history of a post, newest first" }),
  restorePostVersion: route({ method: "POST", path: "/v1/orgs/:orgId/posts/:postId/versions/:versionId/restore", params: OrgPost.extend({ versionId: z.string().uuid() }), body: Empty, response: Post, summary: "Make a version current in Malfak (local only: Instagram does not allow editing a published caption)" }),
  // ---- platform admin
  adminMediaRepair: route({ method: "GET", path: "/v1/admin/media/repair", response: MediaRepairStatus, summary: "Orgs with channel media / avatars not copied into storage yet + mirroring config" }),
  adminRunMediaRepair: route({ method: "POST", path: "/v1/admin/media/repair", body: RunMediaRepairRequest, response: RunMediaRepairResult, status: 202, summary: "Start the media repair (one org or every org); per-org jobs show in each org's jobs center" }),
} as const;

export type PostVersion = z.infer<typeof PostVersion>;
export type PostVersionSource = z.infer<typeof PostVersionSource>;
export type ReloadPostsRequest = z.infer<typeof ReloadPostsRequest>;
export type MediaRepairStatus = z.infer<typeof MediaRepairStatus>;
export type MediaRepairOrg = z.infer<typeof MediaRepairOrg>;
